// Firma única de la API (Carlos 2026-08-22): un DTO de entrada y el header del
// token. Lo que iba en la ruta o en la query viaja ahora en el cuerpo, y los
// parámetros globales -workspace, sprint- los trae la SESIÓN.
/**
 * API client tipado para /service-diagnosis/* — barrido de patrón canónico sobre
 * un SERVICIO completo (visible solo para ARQUITECTO/ADMIN).
 *
 * El diagnóstico por HT mide una feature; esto mide cómo está parado el servicio
 * entero y estima las horas (dev e IA) de cerrar la brecha de patrón.
 */

import { api } from "./client";

export type JobStatus = "queued" | "running" | "done" | "error" | "canceled";

export interface ServiceDiagnosisJob {
  id: string;
  service_id: string;
  service_name: string;
  /** 'full' = las ~50 reglas del patrón; 'rule_sweep' = acotado a `rule_ids`. */
  kind: "full" | "rule_sweep";
  rule_ids: string[];
  branch: string | null;
  status: JobStatus;
  total_packages: number;
  done_packages: number;
  current_package: string | null;
  /** Patrón (regla ASR-PC-*) que se evalúa dentro del paquete en curso. */
  current_rule: string | null;
  cancel_requested: boolean;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  error_detail: string | null;
}

export interface JobEvent {
  id: number;
  created_at: string;
  level: "debug" | "info" | "warn" | "error";
  phase: string | null;
  message: string;
}

export interface PackageSummary {
  id: string;
  orden: number;
  package_rel: string;
  package_name: string;
  excluded: boolean;
  exclusion_reason: string | null;
  ht_code: string | null;
  verdict: "PASS" | "WARN" | "FAIL" | null;
  counts: Record<string, number>;
  horas_dev: number;
  horas_ia: number;
}

export interface PackageAssertion {
  id: string;
  dim: string;
  title: string;
  /** Severidad MOSTRADA (observacional). */
  status: string;
  /** Severidad REAL previa a la degradación; vacío si no hubo. */
  status_original: string;
  detail: string;
  fix: string;
  exclusion: string;
  evidence: string[];
  horas_dev?: number;
}

export interface PackageDetail extends PackageSummary {
  ht_path: string | null;
  assertions: PackageAssertion[];
  markdown: string;
}

export interface RuleEstimate {
  rule_id: string;
  dim: string;
  title: string;
  hallazgos: number;
  horas_dev: number;
  paquetes?: number;
}

export interface Estimation {
  horas_dev: number;
  horas_ia: number;
  factor_ia: number;
  hallazgos: number;
  top_reglas: RuleEstimate[];
  por_dimension: { dim: string; hallazgos: number; horas_dev: number }[];
  reglas_sin_peso: string[];
  banda_contraste: { p50: number; p90: number; n: number } | null;
  paquetes_fuera_de_banda: string[];
  ranking_paquetes: {
    package_rel: string; verdict: string | null; horas_dev: number;
    horas_ia: number; hallazgos: number;
  }[];
}

export interface ServiceRun {
  id: string;
  service_id: string;
  service_name: string;
  branch: string | null;
  commit_sha: string | null;
  created_at: string;
  verdict: "PASS" | "WARN" | "FAIL";
  counts: Record<string, number>;
  packages_total: number;
  packages_analizados: number;
  packages_excluidos: number;
  packages_con_ht: number;
  plan_project_id: string | null;
  params: Record<string, unknown>;
  estimation: Estimation;
  packages: PackageSummary[];
}

export interface ServiceState {
  service_id: string;
  service_name: string;
  /** Rama oficial del service en este workspace: se muestra, no se elige. */
  backend_repo_branch: string;
  has_run: boolean;
  verdict: "PASS" | "WARN" | "FAIL" | null;
  created_at: string | null;
  packages_analizados: number | null;
  horas_dev: number | null;
  horas_ia: number | null;
}

export interface PackageCandidate {
  rel: string;
  name: string;
  excluded: boolean;
  reason: string | null;
  /** Sin entity/ ni repository/: proceso, no CRUD — candidato natural a excluir. */
  solo_controller: boolean;
}

export interface Exclusion {
  package_rel: string;
  reason: string | null;
}

export interface RuleWeight {
  rule_id: string;
  horas_fail: number;
  horas_warn: number;
  nota: string | null;
  updated_at: string;
}

export interface EnqueueIn {
  service_ids: string[];
  branch?: string | null;
  /**
   * Contexto de ejecución OBLIGATORIO (Carlos 2026-08-09). Los tres eran
   * opcionales o inexistentes: el proyecto sólo servía para la banda P50/P90, el
   * workspace se deducía del service y el sprint no se pedía, así que el
   * environment salía de "el sprint más reciente que usa el servicio".
   */
  plan_project_id: string;
  workspace_id: string;
  project_id: string;
  factor_ia?: number;
  exclusions?: Exclusion[];
}

export async function enqueueSweeps(body: EnqueueIn): Promise<ServiceDiagnosisJob[]> {
  const { data } = await api.post<ServiceDiagnosisJob[]>("/service-diagnosis/jobs", body);
  return data;
}

export async function listJobs(): Promise<ServiceDiagnosisJob[]> {
  const { data } = await api.get<ServiceDiagnosisJob[]>("/service-diagnosis/jobs");
  return data;
}

export async function listJobEvents(jobId: string): Promise<JobEvent[]> {
  const { data } = await api.post<JobEvent[]>("/service-diagnosis/jobs/events", { job_id: jobId });
  return data;
}

/** Cancela el barrido en curso o lo saca de la fila. No persiste nada de esa corrida. */
export async function cancelJob(jobId: string): Promise<ServiceDiagnosisJob> {
  const { data } = await api.post<ServiceDiagnosisJob>("/service-diagnosis/jobs/cancel", { job_id: jobId });
  return data;
}

/**
 * Servicios backend DEL WORKSPACE con el estado de su última foto.
 *
 * El workspace es obligatorio: el mismo repo existe como Service distinto en cada
 * workspace (Dev y QA tienen environment y clon propios), y sin filtrar el
 * selector repite el mismo nombre.
 */
export async function listServiceStates(): Promise<ServiceState[]> {
  const { data } = await api.post<ServiceState[]>("/service-diagnosis/services/list", {});
  return data;
}

export async function getServiceRun(serviceId: string): Promise<ServiceRun> {
  const { data } = await api.post<ServiceRun>("/service-diagnosis/runs/detail", { service_id: serviceId });
  return data;
}

export async function getPackageDetail(serviceId: string, packageId: string): Promise<PackageDetail> {
  const { data } = await api.post<PackageDetail>(
    "/service-diagnosis/runs/packages", { service_id: serviceId, package_id: packageId });
  return data;
}

/** JSON autocontenido de la corrida (archivo histórico fuera de la BD). */
export async function exportServiceRun(serviceId: string): Promise<unknown> {
  const { data } = await api.post("/service-diagnosis/runs/export", { service_id: serviceId });
  return data;
}

export async function listCandidatePackages(serviceId: string): Promise<PackageCandidate[]> {
  const { data } = await api.post<PackageCandidate[]>("/service-diagnosis/packages/list", { service_id: serviceId });
  return data;
}

export async function setExclusions(serviceId: string, body: Exclusion[]): Promise<Exclusion[]> {
  const { data } = await api.post<Exclusion[]>("/service-diagnosis/exclusions/set",
    { service_id: serviceId, exclusions: body });
  return data;
}

export async function listWeights(): Promise<RuleWeight[]> {
  const { data } = await api.get<RuleWeight[]>("/service-diagnosis/weights");
  return data;
}

export async function upsertWeight(ruleId: string, body: Omit<RuleWeight, "updated_at">): Promise<RuleWeight> {
  const { data } = await api.post<RuleWeight>("/service-diagnosis/weights/set", { ...body, rule_id: ruleId });
  return data;
}

// ===========================================================================
// Panorama consolidado por proyecto
// ===========================================================================

export interface PanoramaServicio {
  service_id: string;
  service_name: string;
  verdict: "PASS" | "WARN" | "FAIL";
  branch: string | null;
  created_at: string;
  packages_analizados: number;
  packages_excluidos: number;
  packages_con_ht: number;
  counts: Record<string, number>;
  hallazgos: number;
  horas_dev: number;
  horas_ia: number;
  banda_contraste: { p50: number; p90: number; n: number } | null;
  paquetes_fuera_de_banda: string[];
}

export interface PatronPanorama {
  rule_id: string;
  dim: string;
  title: string;
  servicios: string[];
  hallazgos: number;
  horas_dev: number;
}

export interface Panorama {
  plan_project_id: string;
  plan_project_name: string;
  servicios: PanoramaServicio[];
  /**
   * Frente de este panorama. Cada frente es un panorama aparte: no se mezclan
   * mediciones ni cobertura entre backend y frontend.
   */
  frente: string;
  /** Sin barrer, SOLO del frente de este panorama. */
  servicios_sin_barrer: { service_id: string; service_name: string; kind: string }[];
  /**
   * Corridas que suman hallazgos al total pero NO tienen desglose por regla
   * (estimación anterior al catálogo actual). Recalcular la reconstruye.
   */
  servicios_sin_desglose: {
    service_id: string; service_name: string; hallazgos: number; created_at: string;
  }[];
  /** Barridos que no dejaron ningún paquete analizable: no midieron nada. */
  servicios_sin_paquetes: {
    service_id: string; service_name: string; created_at: string;
  }[];
  /** Fecha de la corrida más reciente que compone el consolidado. */
  medido_hasta: string | null;
  totales: {
    servicios_medidos: number; servicios_con_corrida: number;
    horas_dev: number; horas_ia: number;
    hallazgos: number; paquetes_analizados: number;
    /** Totales SIN recortar, para declarar cuántos se están mostrando. */
    patrones_totales: number;
    patrones_sistemicos_totales: number;
    patrones_aislados_totales: number;
    paquetes_criticos_totales: number;
  };
  /** Reglas que se repiten en MÁS DE UN servicio: lo sistémico. */
  patrones_sistemicos: PatronPanorama[];
  /** Reglas concentradas en un solo servicio: lo aislado. */
  patrones_aislados: PatronPanorama[];
  por_dimension: { dim: string; hallazgos: number; horas_dev: number }[];
  paquetes_criticos: {
    package_rel: string; service_name: string; verdict: string | null;
    horas_dev: number; horas_ia: number; hallazgos: number;
  }[];
  reglas_sin_peso: string[];
}

export interface ConsolidatedAiReport {
  id: string;
  generated_at: string;
  provider: string | null;
  content: string;
  tokens_used: number | null;
}

/**
 * Panorama del proyecto EN UN WORKSPACE.
 *
 * `workspaceId` es obligatorio: Dev y QA son universos separados y jamás se
 * mezclan (Carlos 2026-08-09). Un proyecto de Planeación se ejecuta en varios
 * workspaces, así que sin este eje el consolidado sumaba los dos ambientes.
 */
export async function getPanorama(
  planProjectId: string,
): Promise<Panorama> {
  // El workspace lo trae la SESIÓN: ya no viaja por query (Carlos 2026-08-22).
  const { data } = await api.post<Panorama>(
    "/service-diagnosis/consolidated/get", { plan_project_id: planProjectId });
  return data;
}

export async function getConsolidatedAiReport(
  planProjectId: string,
): Promise<ConsolidatedAiReport | null> {
  const { data } = await api.post<ConsolidatedAiReport | null>(
    "/service-diagnosis/consolidated/ai-report/last", { plan_project_id: planProjectId });
  return data;
}

export async function generateConsolidatedAiReport(
  planProjectId: string,
): Promise<ConsolidatedAiReport> {
  // El workspace lo trae la SESIÓN: ya no viaja por query (Carlos 2026-08-22).
  const { data } = await api.post<ConsolidatedAiReport>(
    "/service-diagnosis/consolidated/ai-report", { plan_project_id: planProjectId });
  return data;
}

/** Consolidado en Markdown: panorama en tablas + narrativa IA vigente. */
export async function exportPanoramaMarkdown(
  planProjectId: string,
): Promise<string> {
  // El workspace lo trae la SESIÓN: ya no viaja por query (Carlos 2026-08-22).
  const { data } = await api.post<string>(
    "/service-diagnosis/consolidated/export-md", { plan_project_id: planProjectId });
  return data;
}

/**
 * Repesa las corridas del proyecto con el catálogo de pesos ACTUAL, sin repetir
 * el barrido: la matriz de cada paquete ya está guardada.
 */
export async function recomputeConsolidated(
  planProjectId: string,
): Promise<{ corridas_actualizadas: number; reglas_con_peso: number }> {
  // El workspace lo trae la SESIÓN: ya no viaja por query (Carlos 2026-08-22).
  const { data } = await api.post(
    "/service-diagnosis/consolidated/recompute", { plan_project_id: planProjectId });
  return data;
}

/**
 * Carga el catálogo SUGERIDO de pesos (punto de partida editable). Por defecto
 * no pisa lo ya declarado: solo inserta las reglas que falten.
 */
export async function seedWeights(overwrite = false): Promise<{
  creadas: number; actualizadas: number; conservadas: number; total_catalogo: number;
}> {
  const { data } = await api.post("/service-diagnosis/weights/seed", null, {
    params: { overwrite },
  });
  return data;
}
